import { Box, Paper, IconButton } from '@mui/material';
import { customBgWhite } from '@old/styles/generic-styles';
import { formatDate } from '@old/utils/helper';
import { BaseTable } from '@old/ui/table';
import { GridColDef } from '@mui/x-data-grid-pro';
import viewIcon from '@old/assets/svg/administrators/view.svg';
import editIcon from '@old/assets/svg/administrators/edit.svg';
import { useToggle } from '@old/hooks/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ExtractionAction } from '../exctraction-modal';
import { DeleteExtraction } from '../exctraction-modal/deleteExtraction';
import DeleteIcon from '@old/assets/svg/core/trash-green.svg';
import DeleteDisabledIcon from '@old/assets/svg/core/trash-disabled.svg';
import editDisabledIcon from '@old/assets/svg/SiloBagPage/view/edit-disabled.svg';
import i18n from '../../../../libs/language';
import { silobagDataDetailService } from '@old/services/domain/base/silobag/base/silobag-data-detail';
import { MonitoredWarningExtractionModal } from '../exctraction-modal/monitored-warning-extraction-modal';
import { listMonitoredSilobagProtocols } from '@old/services/domain/base/silobag/base/silobag-protocol';

type SummarySilobagExtractions = Awaited<
  ReturnType<typeof silobagDataDetailService>
>['summary']['silobag_extractions'][0];

type SummaryRemainingStock = Awaited<
  ReturnType<typeof silobagDataDetailService>
>['summary']['remaining_stock'];

type SilobagDetailParams = Parameters<typeof silobagDataDetailService>[0];

type SilobagExtractionsProps = {
  data?: SummarySilobagExtractions;
  isLoading?: boolean;
  siloName?: string;
  writePermission?: boolean;
  active?: boolean;
  siloTrigger: (query: SilobagDetailParams) => void;
  setTypeSnackbar: (type: 'success' | 'error') => void;
  setOpenSnackbar: (val: boolean) => void;
  setSnackbarMessage: (msg: string) => void;
  remainingStock?: SummaryRemainingStock;
  setExtractionSuccess?: (success: boolean) => void;
  siloID: string;
  onOpenNewExtraction?: (acceptProtocolInvalidation: boolean) => void;
  onPerformExtractionReady?: (handler: () => void) => void;
};

export function ExtractionSection({
  data,
  isLoading,
  siloName,
  siloTrigger,
  writePermission,
  setTypeSnackbar,
  setOpenSnackbar,
  setSnackbarMessage,
  remainingStock,
  setExtractionSuccess,
  active = false,
  siloID,
  onOpenNewExtraction,
  onPerformExtractionReady,
}: SilobagExtractionsProps) {
  const [modalExtraction, setModalExtraction] = useToggle(false);
  const [extractionID, setExtractionID] = useState<number>();
  const [type, setType] = useState<'new' | 'view' | 'edit'>('edit');
  const [extractionData, setExtractionData] = useState<SummarySilobagExtractions>();
  const [deleteExtraction, setDeleteExtraction] = useState(false);
  const [extractionIdToDelete, setExtractionIdToDelete] = useState<number>();
  const [localRemainingStock, setLocalRemainingStock] = useState<null | string | number>('0');
  const [openMonitoredWarningModal, setOpenMonitoredWarningModal] = useState(false);
  const [monitoredSilobagsToAlert, setMonitoredSilobagsToAlert] = useState<string[]>([]);
  const enableEditMode = () => setType('edit');
  const handleOpenExtraction = (type: 'new' | 'view' | 'edit', params: SummarySilobagExtractions) => {
    setModalExtraction(true);
    setLocalRemainingStock(remainingStock ?? '');
    setType(type);
    setExtractionData(params);
    setExtractionID(params.id);
  };

  const handleDeleteExtraction = (extractionID: number) => {
    setDeleteExtraction(true);
    setExtractionIdToDelete(extractionID);
  };

  const onOpenNewExtractionRef = useRef(onOpenNewExtraction);
  onOpenNewExtractionRef.current = onOpenNewExtraction;

  const handlePerformExtraction = useCallback(async () => {
    try {
      const { summary } = await listMonitoredSilobagProtocols({ query: { ids: [siloID] } });
      const siloMonitoring = summary?.find((item) => item.silobag_id === siloID);

      if (siloMonitoring?.protocol != null) {
        setMonitoredSilobagsToAlert([siloID]);
        setOpenMonitoredWarningModal(true);
        return;
      }

      onOpenNewExtractionRef.current?.(false);
    } catch {
      onOpenNewExtractionRef.current?.(false);
    }
  }, [siloID]);

  useEffect(() => {
    onPerformExtractionReady?.(handlePerformExtraction);
  }, [handlePerformExtraction, onPerformExtractionReady]);

  const handleConfirmMonitoredExtraction = () => {
    setOpenMonitoredWarningModal(false);
    onOpenNewExtractionRef.current?.(true);
  };

  const rows = data !== undefined ? data : [];

  const columns: GridColDef[] = [
    {
      field: 'extraction_date',
      display: 'flex',
      headerName: i18n.t('app.date'),
      flex: 1.1,
      minWidth: 100,
      renderCell: (params) => <span>{formatDate(params.value)}</span>,
    },
    {
      field: 'freight_transport',
      display: 'flex',
      headerName: 'Int. flete',
      flex: 1,
      minWidth: 80,

      renderCell: (params) => <span>{params.value || '―'}</span>,
    },
    {
      field: 'carrier',
      display: 'flex',
      headerName: i18n.t('app.silobags_extractions.carrier'),
      flex: 1,
      minWidth: 120,

      renderCell: (params) => <span>{params.value || '―'}</span>,
    },
    {
      field: 'driver',
      display: 'flex',
      headerName: i18n.t('app.silobags_extractions.driver'),
      flex: 1,
      minWidth: 90,

      renderCell: (params) => <span>{params.value || '―'}</span>,
    },
    {
      field: 'back_plate',
      display: 'flex',
      headerName: i18n.t('app.silobags_extractions.chassis_patent'),
      flex: 1.8,
      minWidth: 130,
      renderCell: (params) => <span>{params.value || '―'}</span>,
    },
    {
      field: 'trailer_back_plate',
      display: 'flex',
      headerName: i18n.t('app.silobags_extractions.chassis_attached'),
      flex: 1.8,
      minWidth: 147,

      renderCell: (params) => <span>{params.value || '―'}</span>,
    },
    {
      field: 'ctg_document',
      display: 'flex',
      headerName: 'CTG',
      flex: 0.5,
      minWidth: 70,
      renderCell: (params) => <span>{params.value || '―'}</span>,
    },
    {
      field: 'waybill',
      display: 'flex',
      headerName: 'C.Porte',
      flex: 1,
      minWidth: 100,
      renderCell: (params) => <span>{params.value || '―'}</span>,
    },
    {
      field: 'contract',
      display: 'flex',
      headerName: i18n.t('app.silobags_extractions.contract'),
      flex: 1,
      minWidth: 100,
      renderCell: (params) => <span>{params.value || '―'}</span>,
    },
    {
      field: 'destination',
      display: 'flex',
      headerName: 'Destino',
      flex: 1,
      minWidth: 90,

      renderCell: (params) => <span>{params.value || '―'}</span>,
    },
    {
      field: 'gross_weight',
      display: 'flex',
      headerName: i18n.t('app.silobags_extractions.gross') + '(kg)',
      flex: 1.1,
      minWidth: 90,

      renderCell: (params) => (
        <span>
          {params.value !== null
            ? i18n.numberToDelimited(params.value, {
                delimiter: i18n.t('config.delimiter'),
              })
            : '―'}
        </span>
      ),
    },
    {
      field: 'truck_weight',
      display: 'flex',
      headerName: 'Tara (kg)',
      flex: 1,
      minWidth: 90,

      renderCell: (params) => (
        <span>
          {params.value !== null
            ? i18n.numberToDelimited(params.value, {
                delimiter: i18n.t('config.delimiter'),
              })
            : '―'}
        </span>
      ),
    },
    {
      field: 'weight',
      display: 'flex',
      headerName: i18n.t('app.silobags_extractions.liquid') + '(kg)',
      flex: 1.2,
      minWidth: 90,

      renderCell: (params) => (
        <span>
          {i18n.numberToDelimited(params.value, {
            delimiter: i18n.t('config.delimiter'),
          }) || '―'}
        </span>
      ),
    },
    {
      field: 'humidity',
      display: 'flex',
      headerName: i18n.t('app.silobags_extractions.humidity'),
      flex: 1.2,
      minWidth: 120,

      renderCell: (params) => (
        <span>
          {params.value !== null
            ? i18n.numberToDelimited(params.value, {
                delimiter: i18n.t('config.delimiter'),
              })
            : '―'}
        </span>
      ),
    },
    {
      field: 'Acciones',
      display: 'flex',
      headerName: i18n.t('app.actions'),
      flex: 1.5,
      minWidth: 100,
      renderCell: (params) => {
        const disableButton = !active || !writePermission;
        return (
          <Box>
            <div>
              <IconButton onClick={() => handleOpenExtraction('view', params.row)}>
                <img src={viewIcon} alt="eye" />
              </IconButton>
              <IconButton onClick={() => handleOpenExtraction('edit', params.row)} disabled={disableButton}>
                <img src={!disableButton ? editIcon : editDisabledIcon} alt="edit" />
              </IconButton>
              <IconButton disabled={disableButton} onClick={() => handleDeleteExtraction(params.row.id)}>
                <img src={!disableButton ? DeleteIcon : DeleteDisabledIcon} alt="delete" />
              </IconButton>
            </div>
          </Box>
        );
      },
    },
  ];

  return (
    <>
      {openMonitoredWarningModal && (
        <MonitoredWarningExtractionModal
          open={openMonitoredWarningModal}
          setOpen={setOpenMonitoredWarningModal}
          handleSave={handleConfirmMonitoredExtraction}
          monitoredSilobagIds={monitoredSilobagsToAlert}
        />
      )}
      {modalExtraction && (
        <ExtractionAction
          active={active}
          extractionData={extractionData}
          silobagOrExtractionId={extractionID}
          type={type}
          open={modalExtraction}
          setOpen={setModalExtraction}
          siloName={siloName}
          remainingStock={localRemainingStock as string}
          setRemainingStock={setLocalRemainingStock}
          setSnackbarMessage={setSnackbarMessage}
          setTypeSnackbar={setTypeSnackbar}
          setOpenSnackbar={setOpenSnackbar}
          enableEditMode={enableEditMode}
          writePermission={writePermission}
          siloTrigger={siloTrigger}
          setExtractionSuccess={setExtractionSuccess}
        />
      )}
      <DeleteExtraction
        open={deleteExtraction}
        setOpen={setDeleteExtraction}
        extractionId={extractionIdToDelete}
        setSnackbarMessage={setSnackbarMessage}
        setTypeSnackbar={setTypeSnackbar}
        setOpenSnackbar={setOpenSnackbar}
        siloTrigger={siloTrigger}
        siloID={siloID}
      />
      <Paper elevation={0} sx={customBgWhite} style={{ marginTop: 0 }}>
        <Box component="h1" margin={0}>
          {i18n.t('app.silobag_data.extractions')}
        </Box>
        {/* TODO: Se parseo como number[] para safar --> Arreglar */}
        {/* el rows puede ser un objeto cuando es necesario que siempre sea arreglo (posible bug oculto) */}
        <BaseTable
          rows={(rows as number[]) ?? []}
          columns={columns}
          initialPageSize={5}
          loading={isLoading}
          noBorders
        />
      </Paper>
    </>
  );
}
